import { useState } from "react";
import { Clock } from "lucide-react";
import { toast } from "sonner";
import type { TaskSummary } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Panel } from "@/components/ui/Panel";
import { useDeleteTask, useRestoreTask } from "@/hooks/use-tasks";
import { refusalDetail } from "@/services/api-client";
import { momentOf } from "@/lib/day";
import { resolveDraftTitle } from "../layout/draft-title";
import { JURISDICTION_LABEL } from "./DraftDetailsFields";
import { isCompleted } from "./draft-standing";

/** What a set-aside draft was: where it stood, and the office it was for. */
function wasA(draft: TaskSummary): string {
  const stood = isCompleted(draft) ? "Completed draft" : "Draft in progress";
  return draft.jurisdiction === null
    ? stood
    : `${stood} · ${JURISDICTION_LABEL[draft.jurisdiction]}`;
}

/** One draft set aside: what it was called, when it went, what it was, and the two ways out. */
function DeletedRow({
  draft,
  onRestore,
  onRemove,
}: {
  draft: TaskSummary;
  onRestore: () => void;
  onRemove: () => void;
}) {
  return (
    <Panel
      as="li"
      shape="row"
      setAside
      data-testid={`deleted-row-${draft.task_id}`}
      className="flex flex-wrap items-center gap-x-5 gap-y-2"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-bold text-muted-foreground">
          {resolveDraftTitle(draft)}
        </p>
        <p className="mt-1.5 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[12px] text-muted-foreground">
          {draft.deleted_at !== null && (
            <span className="inline-flex items-center gap-[5px]">
              <Clock
                aria-hidden
                className="size-[13px] text-subtle-foreground"
              />
              Deleted {momentOf(draft.deleted_at)}
            </span>
          )}
          <span>{wasA(draft)}</span>
        </p>
      </div>
      <div className="flex gap-2">
        <Button variant="way" onClick={onRestore}>
          Restore
        </Button>
        <Button variant="danger-outline" onClick={onRemove}>
          Permanently Delete
        </Button>
      </div>
    </Panel>
  );
}

/**
 * Brings a draft back from Recently deleted, and says so when it cannot. Each
 * call awaits its own answer, so two made together each say how they went.
 */
function useRestoreDraft() {
  const restore = useRestoreTask();
  return (taskId: string) => {
    restore.mutateAsync(taskId).catch((error: unknown) => {
      toast.error(refusalDetail(error, "The draft could not be restored."));
    });
  };
}

/**
 * The drafts set aside under this client, the Recently deleted tab's list. A
 * draft is restored to the list its status puts it in, or removed for good
 * after the attorney confirms, which cannot be undone.
 */
export function RecentlyDeleted({
  drafts,
}: {
  drafts: readonly TaskSummary[];
}) {
  const [asking, setAsking] = useState<TaskSummary | null>(null);
  const restore = useRestoreDraft();
  const remove = useDeleteTask();
  return (
    <>
      <p className="mx-0.5 mb-[13px] text-detail leading-normal text-muted-foreground">
        Restore an item, or remove it from Patent Studio for good.
      </p>
      <ul className="flex flex-col gap-2.5">
        {drafts.map((draft) => (
          <DeletedRow
            key={draft.task_id}
            draft={draft}
            onRestore={() => {
              restore(draft.task_id);
            }}
            onRemove={() => {
              setAsking(draft);
            }}
          />
        ))}
      </ul>
      <ConfirmDialog
        open={asking !== null}
        destructive
        warn
        title="Permanently delete?"
        description={
          asking === null
            ? ""
            : `"${resolveDraftTitle(asking)}" will be removed from Patent Studio for good.`
        }
        note={
          <>
            It will no longer be possible to restore this draft.{" "}
            <b className="font-bold">This cannot be undone.</b>
          </>
        }
        actionLabel="Permanently Delete"
        onCancel={() => {
          setAsking(null);
        }}
        onConfirm={() => {
          if (asking !== null) {
            remove.mutateAsync(asking.task_id).catch((error: unknown) => {
              toast.error(
                refusalDetail(error, "The draft could not be deleted."),
              );
            });
          }
          setAsking(null);
        }}
      />
    </>
  );
}
